import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Truck, Download as DownloadIcon } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import { Reveal, Stagger, StaggerItem } from '@/components/Reveal';
import CTABand from '@/components/CTABand';
import { useLanguage } from '@/context/LanguageContext';

export default function Offers() {
  const { t } = useLanguage();
  const [vehicles, setVehicles] = useState(25);
  const [annual, setAnnual] = useState(false);
  const cards = t('offers.cards');
  const calc = t('offers.calc');
  const cost = Math.round(vehicles * (Number(calc.pricePerVehicle) || 14) * (annual ? 0.8 : 1));
  const fuelSave = Math.round(vehicles * (Number(calc.fuelPerVehicle) || 120) * ((Number(calc.savingsPct) || 28) / 100));

  return (
    <div className="pt-28" data-testid="offers-page">
      <section className="mx-auto max-w-7xl px-5 pt-8 lg:px-8">
        <SectionHeading eyebrow="offers.eyebrow" title="offers.title" sub="offers.sub" align="center" />

        <Stagger className="mt-14 grid gap-5 lg:grid-cols-3" gap={0.1}>
          {cards.map((card, ci) => (
            <StaggerItem key={card.id || ci}>
              <div className={`relative flex h-full flex-col rounded-3xl border bg-white p-7 transition-all duration-300 ${card.popular ? 'border-brand-blue shadow-[0_36px_70px_-32px_rgba(0,82,255,0.5)]' : 'border-brand-line card-hover'}`} data-testid={`offer-card-${card.id}`}>
                {card.popular && (
                  <span className="absolute -top-3 start-1/2 -translate-x-1/2 rounded-full bg-brand-blue px-4 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white rtl:translate-x-1/2" data-testid="offer-popular-badge">
                    ★
                  </span>
                )}
                <h3 className="font-display text-xl font-extrabold text-brand-ink">{card.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-steel">{card.d}</p>
                <span className="mt-4 inline-flex w-fit rounded-full bg-brand-teal/10 border border-brand-teal/20 px-3.5 py-1.5 font-mono text-[11px] font-bold text-brand-teal" dir="ltr">{card.price}</span>
                {card.expires && <span className="mt-2 font-mono text-[10px] text-brand-dim" dir="ltr" data-testid={`offer-expires-${card.id}`}>Valid until {card.expires}</span>}
                <ul className="mt-6 flex-1 space-y-3">
                  {(card.includes || []).filter(Boolean).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm font-semibold text-brand-steel">
                      <span className="mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-brand-blue/10"><Check className="h-3 w-3 text-brand-blue" /></span>
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 grid gap-2.5">
                  <a
                    href={card.pdf || '#'}
                    download
                    data-testid={`offer-pdf-${card.id}`}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-line py-3 text-sm font-bold text-brand-ink transition-all duration-300 hover:border-brand-blue hover:text-brand-blue"
                  >
                    <DownloadIcon className="h-4 w-4" /> {t('offers.pdf')}
                  </a>
                  <Link
                    to={card.requestLink || '/contact'}
                    data-testid={`offer-request-${card.id}`}
                    className={`rounded-full py-3 text-center text-sm font-bold transition-all duration-300 ${card.popular ? 'bg-brand-blue text-white hover:bg-brand-blueHover hover:shadow-[0_14px_30px_-10px_rgba(0,82,255,0.6)]' : 'bg-brand-ink text-white hover:opacity-90'}`}
                  >
                    {t('offers.request')}
                  </Link>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8" data-testid="calculator-section">
        {t('offers.showCalc') !== false && <Reveal>
          <div className="grid gap-10 rounded-[2rem] border border-brand-line bg-white p-8 shadow-[0_40px_80px_-40px_rgba(15,23,42,0.2)] lg:grid-cols-2 lg:p-12">
            <div>
              <h3 className="font-display text-2xl font-extrabold text-brand-ink">{t('offers.calc.title')}</h3>
              <p className="mt-2 text-sm text-brand-steel">{t('offers.calc.sub')}</p>
              <div className="mt-8 flex items-baseline justify-between">
                <span className="font-mono text-[11px] font-semibold uppercase tracking-widest text-brand-dim">{t('offers.calc.vehicles')}</span>
                <span className="font-display text-3xl font-extrabold text-brand-blue" data-testid="calc-vehicles-value" dir="ltr">{vehicles}</span>
              </div>
              <input
                type="range" min="1" max="500" value={vehicles}
                onChange={(e) => setVehicles(Number(e.target.value))}
                data-testid="calc-vehicles-slider"
                className="mt-3 w-full cursor-pointer"
                style={{ accentColor: '#0052FF' }}
              />
              <div className="mt-2 flex justify-between font-mono text-[10px] text-brand-dim" dir="ltr"><span>1</span><span>500</span></div>
              <p className="mt-6 rounded-xl bg-brand-alt px-4 py-3 text-xs leading-relaxed text-brand-steel">{t('offers.calc.note')}</p>
            </div>
            <div className="flex flex-col justify-center gap-4">
              <div className="rounded-2xl border border-brand-line bg-brand-paper p-6" data-testid="calc-cost-tile">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-brand-dim">{t('offers.calc.monthlyCost')}</p>
                <p className="mt-2 font-display text-4xl font-extrabold text-brand-ink" dir="ltr">${cost.toLocaleString()}</p>
              </div>
              <div className="rounded-2xl border border-brand-emerald/30 bg-brand-emerald/5 p-6" data-testid="calc-savings-tile">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-brand-emerald">{t('offers.calc.fuelSave')}</p>
                <p className="mt-2 font-display text-4xl font-extrabold text-brand-emerald" dir="ltr">${fuelSave.toLocaleString()}</p>
              </div>
              <Link to="/contact" data-testid="calc-cta" className="rounded-full bg-brand-blue py-3.5 text-center text-sm font-bold text-white transition-all duration-300 hover:bg-brand-blueHover">{t('offers.calc.cta')}</Link>
            </div>
          </div>
        </Reveal>}

        {t('offers.showPromo') !== false && <Reveal delay={0.1}>
          <div className="mt-5 flex items-center gap-4 rounded-2xl border border-brand-blue/25 bg-brand-blue/5 p-6" data-testid="offers-promo">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-brand-blue text-white"><Truck className="h-5 w-5" /></span>
            <div>
              <h3 className="font-display text-lg font-bold text-brand-ink">{t('offers.promo.title')}</h3>
              <p className="mt-1 text-sm text-brand-steel">{t('offers.promo.sub')}</p>
            </div>
          </div>
        </Reveal>}
      </section>

      <CTABand />
    </div>
  );
}
